import React from 'react';
import { times } from 'lodash-es';
import { Flex } from '@withjoy/joykit';
import type { Colors } from '@withjoy/joykit/theme';
import { pxToRem } from '@withjoy/joykit/theme';
import type { IconSize } from '@withjoy/joykit/components/IconV2/Icon.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Star } from './Star';
import { TooltipV2Wrapper } from '../TooltipV2Wrapper';

const TOTAL_STARS = 5;

interface StarRatingProps {
  id: string;
  rating?: number;
  showPartialRating?: boolean;
  iconSize?: IconSize;
  showTooltip?: boolean;
  toolTipContent?: React.ReactNode;
  bgColor?: keyof Colors;
  hideNonFilledStars?: boolean;
}

export const StarRating = ({
  id,
  rating = 3.5,
  showPartialRating = true,
  iconSize = 'sm',
  showTooltip = false,
  toolTipContent,
  bgColor = 'mono4',
  hideNonFilledStars = false,
}: StarRatingProps) => {
  const getFilledFraction = useEventCallback(
    (idx: number) => {
      const difference = rating - idx;
      if (difference > 1 || (difference > 0 && !showPartialRating)) {
        return 1;
      } else if (difference < 0) {
        return 0;
      }
      return difference;
    },
    [rating, showPartialRating],
  );

  const filledStarsCount = showPartialRating ? Math.ceil(rating) : Math.floor(rating);
  const totalStars = hideNonFilledStars ? filledStarsCount : TOTAL_STARS;

  return (
    <TooltipV2Wrapper
      disabled={!showTooltip}
      overrides={{
        Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white' } },
      }}
      content={toolTipContent}
      onMouseEnterDelay={500}
    >
      <Flex
        columnGap={pxToRem(2)}
        justifyContent="flex-end"
      >
        {times(totalStars, idx => {
          return (
            <Star
              key={`${id}-${idx}`}
              id={`${id}-${idx}`}
              bgColor={bgColor}
              fillColor="mono14"
              iconSize={iconSize}
              colorFilledFraction={getFilledFraction(idx)}
            />
          );
        })}
      </Flex>
    </TooltipV2Wrapper>
  );
};
